import { searchStocks, updateStock } from '@/adapters/stock';
import { IStock, IStockRes, IStockUpdateRes } from '@/interfaces/stock';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Flex, notification, Select, Table, TableColumnsType } from 'antd';
import { useEffect, useMemo, useState } from 'react';

const StockPage: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<IStock[]>([]);
  const [action] = useState<'create' | 'update'>('create');
  const [selected, setSelected] = useState<IStock[]>([]);

  const { data, refetch } = useQuery<IStockRes, Error>({
    queryKey: ['search-stock-list'],
    queryFn: searchStocks,
    retry: 0,
  });

  const actionStockMutation = useMutation({
    mutationFn: async (data: Partial<IStock>[]) => await updateStock(data),
    mutationKey: ['add-stock'],
    onSuccess: ({ data }) => {
      data.forEach((d: IStockUpdateRes) => {
        if (d.status === 'fulfilled') {
          notification.success({ message: 'Sync Stock สำเร็จ' });
        } else {
          notification.error({ message: 'Sync Stock ไม่สำเร็จ' });
        }
      });
      refetch();
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const handleSyncSelectedStocks = async () => {
    await actionStockMutation.mutateAsync(selectedRows);
  };

  const columns = useMemo(
    () =>
      [
        {
          title: 'ลำดับ',
          dataIndex: '',
          width: 40,
          align: 'center' as const,
          className: 'text-bold',
          render: (_: any, __: IStock, i: number) => (
            <div className="font-weight-bold">{i + 1}</div>
          ),
        },
        {
          title: 'คลัง',
          dataIndex: 'LD_LOC',
          className: 'fw-semibold',
        },
        {
          title: 'รหัสสินค้า',
          dataIndex: 'LD_PART',
          className: 'fw-semibold',
        },
        {
          title: 'รายละเอียด 1',
          dataIndex: 'PT_DESC1',
          className: 'fw-semibold',
        },
        {
          title: 'รายละเอียด 2',
          dataIndex: 'PT_DESC2',
        },
        {
          title: 'รายละเอียด 3',
          dataIndex: 'PT_DESC3',
        },
        {
          title: 'ขนาด',
          dataIndex: 'PT_PTSIZE',
        },
        {
          title: 'สี',
          dataIndex: 'PT_COLOR',
        },
        {
          title: 'หน่วยสินค้า',
          dataIndex: 'LD_UM',
        },
        {
          title: 'จำนวน',
          dataIndex: 'LD_QUANTITY',
        },

        {
          title: 'Message',
          dataIndex: 'LOC_SING_MESSAGE',
        },
      ] as TableColumnsType<IStock>,
    [],
  );

  useEffect(() => {
    const list = action === 'create' ? data?.data?.insert : data?.data?.update;
    setSelected(list || []);
    setSelectedRowKeys([]);
  }, [action, data?.data?.insert, data?.data?.update]);

  const rowSelection = {
    onChange: (_: React.Key[], selectedRows: IStock[]) => {
      setSelectedRowKeys(selectedRows.map(row => row.key as string));
      setSelectedRows(selectedRows);
    },
    selectedRowKeys,
  };

  const [searchLocFrom, setSearchLocFrom] = useState('');
  const [searchLocTo, setSearchLocTo] = useState('');
  const [searchPartFrom, setSearchPartFrom] = useState('');
  const [searchPartTo, setSearchPartTo] = useState('');

  // Helper function to check if a value is between two search terms (inclusive)
  const isBetween = (
    value: string,
    fromValue: string,
    toValue: string,
  ): boolean => {
    const valueUpper = (value || '').toUpperCase();
    const fromUpper = fromValue.trim().toUpperCase() || '';
    const toUpper = toValue.trim().toUpperCase() || 'ZZZZZZZZZZZZZZZZZZZZ';

    // If both are empty, return all
    if (!fromUpper && toUpper === 'ZZZZZZZZZZZZZZZZZZZZ') {
      return true;
    }

    // If only one has a value, check if contains
    if (fromUpper && !toValue.trim()) {
      return valueUpper.includes(fromUpper);
    }

    if (!fromUpper && toValue.trim()) {
      return valueUpper.includes(toUpper);
    }

    // Check if any substring is between the range
    for (let i = 0; i < valueUpper.length; i++) {
      for (let j = i + 1; j <= valueUpper.length; j++) {
        const subValue = valueUpper.substring(i, j);
        if (subValue >= fromUpper && subValue <= toUpper) {
          return true;
        }
      }
    }

    return false;
  };

  // Filter selected data by search criteria
  const filteredData = useMemo(() => {
    return selected
      .filter(item => {
        // Apply both filters - LD_LOC and LD_PART
        const locMatch = isBetween(item.LD_LOC, searchLocFrom, searchLocTo);
        const partMatch = isBetween(item.LD_PART, searchPartFrom, searchPartTo);

        // Both conditions must be satisfied
        return locMatch && partMatch;
      })
      .map((i, idx) => {
        i.key = (idx + 1).toString();
        return i;
      });
  }, [selected, searchLocFrom, searchLocTo, searchPartFrom, searchPartTo]);

  return (
    <div>
      <Flex justify="space-between" gap={12} className="mb-3">
        <Flex align="start" gap={12}>
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ minWidth: '70px' }}>คลัง จาก:</div>
              <Select
                style={{ minWidth: 150 }}
                value={searchLocFrom}
                onChange={e => {
                  setSearchLocFrom(e || '');
                }}
                size="large"
                showSearch
                allowClear
                filterOption={(input, option) =>
                  ((option?.label as string) ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={Array.from(new Set(selected.map(item => item.LD_LOC)))
                  .sort()
                  .map(code => ({
                    label: code,
                    value: code,
                  }))}
              />

              <div>ถึง:</div>
              <Select
                style={{ minWidth: 150 }}
                value={searchLocTo}
                onChange={e => {
                  setSearchLocTo(e || '');
                }}
                size="large"
                showSearch
                allowClear
                filterOption={(input, option) =>
                  ((option?.label as string) ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={Array.from(new Set(selected.map(item => item.LD_LOC)))
                  .sort()
                  .map(code => ({
                    label: code,
                    value: code,
                  }))}
              />
              <div style={{ minWidth: '70px' }}>สินค้า จาก:</div>
              <Select
                style={{ minWidth: 150 }}
                value={searchPartFrom}
                onChange={e => {
                  setSearchPartFrom(e || '');
                }}
                size="large"
                showSearch
                allowClear
                filterOption={(input, option) =>
                  ((option?.label as string) ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={Array.from(new Set(selected.map(item => item.LD_PART)))
                  .sort()
                  .map(code => ({
                    label: code,
                    value: code,
                  }))}
              />
              <div>ถึง:</div>
              <Select
                style={{ minWidth: 150 }}
                value={searchPartTo}
                onChange={e => {
                  setSearchPartTo(e || '');
                }}
                size="large"
                showSearch
                allowClear
                filterOption={(input, option) =>
                  ((option?.label as string) ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={Array.from(new Set(selected.map(item => item.LD_PART)))
                  .sort()
                  .map(code => ({
                    label: code,
                    value: code,
                  }))}
              />
            </div>
          </div>
        </Flex>
        <button
          type="button"
          className="btn btn-success"
          style={{ minWidth: 150 }}
          disabled={!selectedRowKeys.length}
          onClick={() => handleSyncSelectedStocks()}
        >
          {actionStockMutation.isPending ? (
            <>
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
              <span className="sr-only">Loading...</span>
            </>
          ) : (
            'Sync '
          )}
        </button>
      </Flex>
      <Table<IStock>
        rowSelection={{ type: 'checkbox', ...rowSelection }}
        columns={columns}
        dataSource={filteredData}
        size="small"
        bordered
        pagination={false}
        locale={{ emptyText: 'ไม่พบรายการ' }}
      />
    </div>
  );
};

export default StockPage;
